/**
 * API service layer for backend communication
 * Centralizes all API calls with authentication support
 */

import { Dealership, HistoricalData, ApiResponse, MonthlyTotals } from '../types';
import {
  DailyProgressionResponse,
  DailyGrossSummary,
  YearComparisonData,
  MultiYearAverage,
  ProjectionTargetData,
  UnprocessedDealsSummary,
} from '../types/forecast';

const API_URL = process.env.REACT_APP_API_URL || '';

// Token getter function - will be set by AuthContext
// Accepts optional forceRefresh parameter to bypass MSAL cache
let getAccessTokenFn: ((forceRefresh?: boolean) => Promise<string | null>) | null = null;

/**
 * Set the token getter function (called from AuthContext)
 */
export function setTokenGetter(fn: (forceRefresh?: boolean) => Promise<string | null>) {
  getAccessTokenFn = fn;
}

// Track if we're currently refreshing token to avoid multiple refresh attempts
let isRefreshingToken = false;
let tokenRefreshPromise: Promise<string | null> | null = null;

/**
 * Force refresh the token (bypasses MSAL cache and gets new token from Azure AD)
 */
async function forceRefreshToken(): Promise<string | null> {
  if (isRefreshingToken && tokenRefreshPromise) {
    return tokenRefreshPromise;
  }

  isRefreshingToken = true;
  tokenRefreshPromise = (async () => {
    try {
      if (getAccessTokenFn) {
        // Pass forceRefresh=true to bypass MSAL's token cache
        // This ensures we get a fresh token from Azure AD when the backend rejects the current one
        return await getAccessTokenFn(true);
      }
      return null;
    } finally {
      isRefreshingToken = false;
      tokenRefreshPromise = null;
    }
  })();

  return tokenRefreshPromise;
}

/**
 * Generic API request handler with error handling and authentication
 * Automatically retries with fresh token on 401/403 errors
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  isRetry: boolean = false
): Promise<ApiResponse<T>> {
  // 35s timeout — 5s margin beyond API Gateway's 30s hard limit
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 35000);

  try {
    // Get access token if available
    let authHeaders: Record<string, string> = {};
    if (getAccessTokenFn) {
      const token = await getAccessTokenFn();
      if (token) {
        authHeaders['Authorization'] = `Bearer ${token}`;
      }
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers,
      },
    });

    if (!response.ok) {
      // Handle 401/403 - token may be expired
      if ((response.status === 401 || response.status === 403) && !isRetry) {
        console.warn('Token may be expired, attempting refresh...');
        const newToken = await forceRefreshToken();
        if (newToken) {
          return apiRequest<T>(endpoint, options, true);
        }
        console.error('Authentication required - token refresh failed');
      }

      // Try to parse JSON error body, but don't fail if response is HTML (e.g. 503)
      let errorMessage = `HTTP error ${response.status}`;
      try {
        const data = await response.json();
        errorMessage = data.error || errorMessage;
      } catch { /* non-JSON body (e.g. API Gateway 503 HTML) */ }

      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  } catch (error: any) {
    console.error(`API request failed: ${endpoint}`, error);
    return {
      success: false,
      error: error.name === 'AbortError' ? 'Request timed out' : (error.message || 'Network error occurred'),
    };
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Fetches list of dealership locations
 */
export async function getDealerships(): Promise<ApiResponse<Dealership[]>> {
  return apiRequest<Dealership[]>('/dealerships');
}

/**
 * Job status response interface
 */
export interface JobStatusData {
  jobId: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  progress: number;
  message: string;
  locationId: number;
  year: number;
  month?: number;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  error?: string;
  result?: {
    locationId: number;
    locationName: string;
    year: number;
    accountsLoaded: number;
    loadedAt: string;
  };
}

/**
 * Triggers async historical data load for a location, year, and optionally month
 * Returns job ID immediately for polling
 */
export async function loadHistoricalData(
  locationId: number,
  year: number,
  month?: number
): Promise<ApiResponse<{ jobId: string; status: string; progress: number; message: string }>> {
  const body: any = { locationId, year };
  if (month !== undefined) {
    body.month = month;
  }

  return apiRequest('/historical-data/load', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Gets the status of a data load job
 */
export async function getJobStatus(jobId: string): Promise<ApiResponse<JobStatusData>> {
  return apiRequest<JobStatusData>(`/historical-data/load/status/${jobId}`);
}

/**
 * Retrieves historical data from DynamoDB
 */
export async function getHistoricalData(
  locationId: number,
  year: number,
  month?: number
): Promise<ApiResponse<HistoricalData>> {
  let url = `/historical-data?locationId=${locationId}&year=${year}`;
  if (month !== undefined) {
    url += `&month=${month}`;
  }

  return apiRequest<HistoricalData>(url);
}

/**
 * Unit counts response interface
 */
export interface UnitCountsData {
  locationId: number;
  locationName: string;
  year: number;
  month?: number;
  reportType: string;
  newVehicleUnits: Record<string, number>;
  usedVehicleUnits: Record<string, number>;
  totalNewUnits: number;
  totalUsedUnits: number;
  // Monthly breakdown fields
  monthlyNewUnits?: MonthlyTotals;
  monthlyUsedUnits?: MonthlyTotals;
  monthlyNewUnitsByBrand?: Record<string, MonthlyTotals>;
  monthlyUsedUnitsByBrand?: Record<string, MonthlyTotals>;
}

/**
 * Retrieves unit counts from DealerBuilt database
 */
export async function getUnitCounts(
  locationId: number,
  year: number,
  month?: number
): Promise<ApiResponse<UnitCountsData>> {
  let url = `/unit-counts?locationId=${locationId}&year=${year}`;
  if (month !== undefined) {
    url += `&month=${month}`;
  }

  return apiRequest<UnitCountsData>(url);
}

// ============================================
// DAILY GROSS PROGRESSION (Forecast Page)
// ============================================

/**
 * API response for daily progression endpoint
 */
export interface DailyProgressionApiResponse {
  locationId: number;
  locationName: string;
  year: number;
  month: number;
  currentMonth: DailyGrossSummary | null;
  historical: Record<number, DailyGrossSummary | null>;
  availableYears: number[];
  workingDays: {
    total: number;
    elapsed: number;
    remaining: number;
    currentDayNumber: number;
  };
  // Department-specific working days for display (e.g., SERVICE has fewer days than Sales)
  displayWorkingDays?: {
    total: number;
    elapsed: number;
    remaining: number;
  };
  comparison: {
    daysRemaining: number;
    byYear: Record<number, YearComparisonData>;
    average: MultiYearAverage;
  };
  projectionTargets?: ProjectionTargetData;
  unprocessedDeals?: UnprocessedDealsSummary;
  serviceGrossByPayType?: {
    customer: number;
    warranty: number;
    internal: number;
    other: number;
  };
  serviceControllableExpenses?: Array<{
    accountCode: string;
    description: string;
    amount: number;
  }>;
  partsControllableExpenses?: Array<{
    accountCode: string;
    description: string;
    amount: number;
  }>;
  partsGrossByPayType?: {
    customerParts: number;
    warrantyParts: number;
    internalParts: number;
    counterRetail: number;
    counterWholesale: number;
    otherParts: number;
    inventoryAdjustment: number;
  };
  partsSalesByPayType?: {
    customerParts: number;
    warrantyParts: number;
    internalParts: number;
    counterRetail: number;
    counterWholesale: number;
    otherParts: number;
  };
  /** Body Shop gross split into Labor (45x/65x) vs Parts (47x/67x) — locs 1, 12, 44 only */
  bodyShopGrossByCategory?: {
    labor: number;
    parts: number;
  };
  consolidatedServiceByLocation?: Array<{
    locationId: number;
    locationName: string;
    serviceGrossByPayType: { customer: number; warranty: number; internal: number; other: number };
    totalServiceGross: number;
    projectedGross: number | null;
    controllableExpenses: Array<{ accountCode: string; description: string; amount: number }>;
    totalControllableExpenses: number;
    controllableExpensePct: number;
    closedROCount: number;
    openROCount: number;
    openRate: number;
    appointmentsNext5Days?: Array<{ date: string; dayLabel: string; count: number; capacity?: number }>;
    appointmentsNext5DaysTotal?: number;
    appointmentsNext5DaysCapacityTotal?: number;
  }>;
  consolidatedPartsByLocation?: Array<{
    locationId: number;
    locationName: string;
    partsGrossByPayType: {
      customerParts: number;
      warrantyParts: number;
      internalParts: number;
      counterRetail: number;
      counterWholesale: number;
      otherParts: number;
    };
    totalPartsGross: number;
    projectedGross: number | null;
    controllableExpenses: Array<{ accountCode: string; description: string; amount: number }>;
    totalControllableExpenses: number;
    controllableExpensePct: number;
  }>;
  /** Nightly blue tile snapshots for accurate chart history */
  dailySnapshots?: Array<{
    workingDay: number;
    grossByDepartment: {
      NEW_VEHICLE: number;
      USED_VEHICLE: number;
      SERVICE: number;
      PARTS: number;
      BODY_SHOP: number;
      RENTAL: number;
      OTHERS: number;
      ALL: number;
    };
  }>;
  /**
   * Consolidated trending = sum of each location's individually-computed trending.
   * Sum-of-trends, not trend-of-sum. Only present for consolidated current/pre-close-prev month.
   */
  consolidatedTrending?: {
    byDepartment: {
      NEW_VEHICLE: number;
      USED_VEHICLE: number;
      SERVICE: number;
      PARTS: number;
      BODY_SHOP: number;
      RENTAL: number;
      OTHERS: number;
      ALL: number;
    };
  };
  /**
   * Per-department trending for a single location (each dept uses its own working days).
   * ALL = sum of the 7 departments. Only present for individual locations on current month.
   */
  trendingByDepartment?: {
    NEW_VEHICLE: number;
    USED_VEHICLE: number;
    SERVICE: number;
    PARTS: number;
    BODY_SHOP: number;
    RENTAL: number;
    OTHERS: number;
    ALL: number;
  };
}

/**
 * Retrieves daily gross progression data for the Forecast page.
 * Returns current month data plus historical comparisons.
 *
 * @param locationId - Dealership location ID
 * @param year - Year to fetch
 * @param month - Month to fetch (1-12)
 * @param department - Department for working days calculation (optional, defaults to NEW_VEHICLE)
 */
export async function getDailyProgression(
  locationId: number,
  year: number,
  month: number,
  department?: string
): Promise<ApiResponse<DailyProgressionApiResponse>> {
  let url = `/daily-progression?locationId=${locationId}&year=${year}&month=${month}`;
  if (department) {
    url += `&department=${department}`;
  }
  return apiRequest<DailyProgressionApiResponse>(url);
}

/**
 * API response for daily gross load endpoint
 */
export interface LoadDailyGrossResponse {
  success: boolean;
  data?: DailyGrossSummary;
  message: string;
  fromCache?: boolean;
}

/**
 * Loads daily gross data from DealerBuilt for a specific location/year/month.
 * Data is cached in DynamoDB after first load.
 * Historical months (past years) are cached permanently.
 * Current month can be refreshed with forceRefresh=true.
 *
 * NOTE: This is the synchronous version - may timeout for large datasets.
 * Use loadDailyGrossAsync for historical data loading.
 */
export async function loadDailyGross(
  locationId: number,
  year: number,
  month: number,
  forceRefresh: boolean = false,
  triggerSnapshotWriter: boolean = false
): Promise<ApiResponse<LoadDailyGrossResponse>> {
  return apiRequest<LoadDailyGrossResponse>('/daily-gross/load', {
    method: 'POST',
    body: JSON.stringify({
      locationId,
      year,
      month,
      forceRefresh,
      triggerSnapshotWriter,
    }),
  });
}

/**
 * Response from async daily gross load trigger
 */
export interface TriggerDailyGrossResponse {
  jobId: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  progress: number;
  message: string;
}

/**
 * Triggers async daily gross data load. Returns immediately with a job ID.
 * Use getJobStatus to poll for completion.
 *
 * This avoids API Gateway timeout issues for large datasets.
 */
export async function loadDailyGrossAsync(
  locationId: number,
  year: number,
  month: number,
  forceRefresh: boolean = false
): Promise<ApiResponse<TriggerDailyGrossResponse>> {
  return apiRequest<TriggerDailyGrossResponse>('/daily-gross/load/async', {
    method: 'POST',
    body: JSON.stringify({
      locationId,
      year,
      month,
      forceRefresh,
    }),
  });
}

/**
 * Helper function to wait for a job to complete with polling.
 * Returns when job is COMPLETED or FAILED.
 */
export async function waitForJob(
  jobId: string,
  onProgress?: (progress: number, message: string) => void,
  pollIntervalMs: number = 2000,
  maxWaitMs: number = 300000 // 5 minutes max
): Promise<ApiResponse<JobStatusData>> {
  const startTime = Date.now();

  while (Date.now() - startTime < maxWaitMs) {
    const response = await getJobStatus(jobId);

    if (!response.success || !response.data) {
      return response;
    }

    const { status, progress, message } = response.data;

    // Report progress
    if (onProgress) {
      onProgress(progress, message);
    }

    // Check if done
    if (status === 'COMPLETED' || status === 'FAILED') {
      return response;
    }

    // Wait before next poll
    await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
  }

  // Timeout
  return {
    success: false,
    error: 'Job timed out waiting for completion'
  };
}

// ============================================
// CONFIGURATION API
// ============================================

/** Saturday schedule type for a location */
export type SaturdayScheduleType = 'ALL_OPEN' | 'SALES_ONLY' | 'ALL_CLOSED';

/** Per-department Saturday schedule */
export interface SaturdayDepartmentSchedule {
  NEW_VEHICLE: boolean;
  USED_VEHICLE: boolean;
  SERVICE: boolean;
  PARTS: boolean;
  BODY_SHOP: boolean;
  RENTAL: boolean;
}

/** Holiday closure type */
export type HolidayClosureType = 'ALL' | 'PARTIAL_SALES_ONLY';

/** Holiday date calculation type */
export type HolidayDateType = 'FIXED' | 'NTH_WEEKDAY' | 'LAST_WEEKDAY';

/** Location schedule config from API */
export interface LocationScheduleConfig {
  locationId: number;
  locationName: string;
  saturdaySchedule: SaturdayDepartmentSchedule;
}

/** Holiday config from API */
export interface HolidayConfig {
  name: string;
  displayName: string;
  closureType: HolidayClosureType;
  dateType: HolidayDateType;
  month?: number;
  day?: number;
  weekNumber?: number;
  dayOfWeek?: number;
}

/** TSM group (Service Advisor bucket for the TSM Group Ranking Report) */
export type TsmGroup = 'Domestic' | 'Import' | 'Express';
export const TSM_GROUPS: TsmGroup[] = ['Domestic', 'Import', 'Express'];

/** TSM group assignment record returned by GET /config */
export interface TsmGroupAssignmentConfig {
  locationId: number;
  advisorId: string;
  advisorName: string;
  group: TsmGroup;
}

/** One Nut Sheet Type drop-down entry — one of these is selected per transaction row. */
export interface NutSheetTypeDef {
  code: string;  // e.g., "FC"
  label: string; // e.g., "NEW FORD CAR"
}

/** Per-location Type lists for the Nut Sheet page. */
export interface NutSheetTypesConfig {
  locationId: number;
  newTypes: NutSheetTypeDef[];
  usedTypes: NutSheetTypeDef[];
}

/** Request to save a location's Nut Sheet Type lists. */
export interface SaveNutSheetTypesRequest {
  type: 'NUT_SHEET_TYPES';
  locationId: number;
  newTypes: NutSheetTypeDef[];
  usedTypes: NutSheetTypeDef[];
}

/** Sales team assignment record returned by GET /config (team = sales manager). */
export interface SalesTeamAssignmentConfig {
  locationId: number;
  salespersonId: string;
  salespersonName: string;
  dmsSalesRepId?: string;
  managerId: string;
  managerName: string;
}

/** Request to save a sales team assignment (reassignment overwrites). */
export interface SaveSalesTeamAssignmentRequest {
  type: 'SALES_TEAM_ASSIGNMENT';
  locationId: number;
  salespersonId: string;
  salespersonName: string;
  dmsSalesRepId?: string;
  managerId: string;
  managerName: string;
}

/** Working days configuration response */
export interface WorkingDaysConfigResponse {
  locationSchedules: LocationScheduleConfig[];
  holidays: HolidayConfig[];
  tsmGroupAssignments: TsmGroupAssignmentConfig[];
  nutSheetTypes: NutSheetTypesConfig[];
  salesTeamAssignments: SalesTeamAssignmentConfig[];
}

/** Request to save a location schedule */
export interface SaveLocationScheduleRequest {
  type: 'LOCATION_SCHEDULE';
  locationId: number;
  locationName: string;
  saturdaySchedule: SaturdayDepartmentSchedule;
}

/** Request to save a holiday */
export interface SaveHolidayRequest {
  type: 'HOLIDAY';
  name: string;
  displayName: string;
  closureType: HolidayClosureType;
  dateType: HolidayDateType;
  month?: number;
  day?: number;
  weekNumber?: number;
  dayOfWeek?: number;
}

/** Request to save a TSM group assignment */
export interface SaveTsmGroupAssignmentRequest {
  type: 'TSM_GROUP_ASSIGNMENT';
  locationId: number;
  advisorId: string;
  advisorName: string;
  group: TsmGroup;
}

/**
 * Fetches working days configuration (location schedules and holidays)
 */
export async function getWorkingDaysConfig(
  forceRefresh: boolean = false
): Promise<ApiResponse<WorkingDaysConfigResponse>> {
  const params = forceRefresh ? '?refresh=true' : '';
  return apiRequest<WorkingDaysConfigResponse>(`/config${params}`);
}

/**
 * Saves a location schedule (Executive role required)
 */
export async function saveLocationSchedule(
  request: SaveLocationScheduleRequest
): Promise<ApiResponse<{ success: boolean; message: string }>> {
  return apiRequest<{ success: boolean; message: string }>('/config', {
    method: 'PUT',
    body: JSON.stringify(request),
  });
}

/**
 * Saves a holiday (Executive role required)
 */
export async function saveHoliday(
  request: SaveHolidayRequest
): Promise<ApiResponse<{ success: boolean; message: string }>> {
  return apiRequest<{ success: boolean; message: string }>('/config', {
    method: 'PUT',
    body: JSON.stringify(request),
  });
}

/**
 * Deletes a holiday (Executive role required)
 */
export async function deleteHoliday(
  holidayName: string
): Promise<ApiResponse<{ success: boolean; message: string }>> {
  const configId = holidayName.toUpperCase().replace(/\s+/g, '_');
  return apiRequest<{ success: boolean; message: string }>(
    `/config?configType=HOLIDAY&configId=${encodeURIComponent(configId)}`,
    { method: 'DELETE' }
  );
}

/**
 * Saves a TSM group assignment (Admin/CEO/Executive role required).
 *
 * Drives the TSM Group Ranking Report (Reports → Service tab) — buckets each
 * advisor at each location into Domestic / Import / Express. Advisors without
 * a saved assignment fall into the "Unassigned" bucket on the report.
 */
export async function saveTsmGroupAssignment(
  request: SaveTsmGroupAssignmentRequest
): Promise<ApiResponse<{ success: boolean; message: string }>> {
  return apiRequest<{ success: boolean; message: string }>('/config', {
    method: 'PUT',
    body: JSON.stringify(request),
  });
}

/**
 * Deletes a TSM group assignment (Admin/CEO/Executive role required).
 *
 * Clearing an assignment moves the advisor back into the Unassigned bucket on
 * the TSM Group Ranking Report.
 */
export async function deleteTsmGroupAssignment(
  locationId: number,
  advisorId: string
): Promise<ApiResponse<{ success: boolean; message: string }>> {
  const configId = `${locationId}_${advisorId}`;
  return apiRequest<{ success: boolean; message: string }>(
    `/config?configType=TSM_GROUP_ASSIGNMENT&configId=${encodeURIComponent(configId)}`,
    { method: 'DELETE' }
  );
}

/**
 * Saves a sales team assignment (Admin/CEO/Executive role required).
 *
 * Assigns a salesperson to a sales manager's team at one location — drives the
 * Sales Team Report (Reports → New Vehicles tab). Reassignment overwrites the
 * same record; salespeople without an assignment fall into the report's
 * "Unassigned" bucket.
 */
export async function saveSalesTeamAssignment(
  request: SaveSalesTeamAssignmentRequest
): Promise<ApiResponse<{ success: boolean; message: string }>> {
  return apiRequest<{ success: boolean; message: string }>('/config', {
    method: 'PUT',
    body: JSON.stringify(request),
  });
}

/**
 * Deletes a sales team assignment (Admin/CEO/Executive role required).
 *
 * Clearing an assignment moves the salesperson back into the Unassigned bucket
 * on the Sales Team Report.
 */
export async function deleteSalesTeamAssignment(
  locationId: number,
  salespersonId: string
): Promise<ApiResponse<{ success: boolean; message: string }>> {
  const configId = `${locationId}_${salespersonId}`;
  return apiRequest<{ success: boolean; message: string }>(
    `/config?configType=SALES_TEAM_ASSIGNMENT&configId=${encodeURIComponent(configId)}`,
    { method: 'DELETE' }
  );
}

/**
 * Saves the Nut Sheet Type lists for one location (Admin/CEO/Executive only).
 */
export async function saveNutSheetTypes(
  request: SaveNutSheetTypesRequest
): Promise<ApiResponse<{ success: boolean; message: string }>> {
  return apiRequest<{ success: boolean; message: string }>('/config', {
    method: 'PUT',
    body: JSON.stringify(request),
  });
}

/**
 * Deletes the Nut Sheet Type lists for one location.
 */
export async function deleteNutSheetTypes(
  locationId: number
): Promise<ApiResponse<{ success: boolean; message: string }>> {
  return apiRequest<{ success: boolean; message: string }>(
    `/config?configType=NUT_SHEET_TYPES&configId=${encodeURIComponent(String(locationId))}`,
    { method: 'DELETE' }
  );
}

// ============================================
// SQUEEZE CONFIGURATION API
// ============================================

/** Squeeze config for a location/brand */
export interface SqueezeConfig {
  locationId: number;
  brand: string;
  laborAccountSuffixes: string[];
  partsAccountSuffixes: string[];
  warrantyLaborRate: number;
  doorRate: number;
  warrantyPartsMarkupPct: number;
  standardPartsMarkupPct: number;
}

/** Response from get squeeze config endpoint */
export interface GetSqueezeConfigResponse {
  squeezeConfigs: SqueezeConfig[];
}

/** Request to save a squeeze config */
export interface SaveSqueezeConfigRequest {
  type: 'SQUEEZE_CONFIG';
  locationId: number;
  brand: string;
  laborAccountSuffixes: string[];
  partsAccountSuffixes: string[];
  warrantyLaborRate: number;
  doorRate: number;
  warrantyPartsMarkupPct: number;
  standardPartsMarkupPct: number;
}

/**
 * Fetches all squeeze configurations
 */
export async function getSqueezeConfigs(
  locationId?: number
): Promise<ApiResponse<GetSqueezeConfigResponse>> {
  const params = locationId ? `?locationId=${locationId}` : '';
  return apiRequest<GetSqueezeConfigResponse>(`/config/squeeze${params}`);
}

/**
 * Saves a squeeze configuration (Executive role required)
 */
export async function saveSqueezeConfig(
  request: SaveSqueezeConfigRequest
): Promise<ApiResponse<{ success: boolean; message: string }>> {
  return apiRequest<{ success: boolean; message: string }>('/config/squeeze', {
    method: 'PUT',
    body: JSON.stringify(request),
  });
}

/**
 * Deletes a squeeze configuration (Executive role required)
 */
export async function deleteSqueezeConfig(
  locationId: number,
  brand: string
): Promise<ApiResponse<{ success: boolean; message: string }>> {
  return apiRequest<{ success: boolean; message: string }>('/config/squeeze', {
    method: 'DELETE',
    body: JSON.stringify({ locationId, brand }),
  });
}

// ========================================
// SERVICE ADVISOR PERFORMANCE
// ========================================

/**
 * Age distribution of open ROs (non-overlapping buckets)
 */
export interface OpenROAgeBuckets {
  under30: number;
  over30: number;
  over60: number;
  over90: number;
  over120: number;
}

/**
 * Service Advisor Performance API Response
 * Matches the backend response structure
 */
export interface ServiceAdvisorPerformanceResponse {
  locationId: number;
  year: number;
  month: number;
  summary: {
    advisorCount: number;
    totalLaborGross: number;
    totalLaborSales: number;
    totalFlatRateHours: number;
    totalROCount: number;
    totalOpenROCount: number;
    totalOpenROAgeBuckets?: OpenROAgeBuckets;
    subletSales?: number;
    subletCost?: number;
    subletGross?: number;
    lastUpdated: string;
  } | null;
  advisors: Array<{
    advisorId: string;
    advisorName: string;
    locationId: number;
    year: number;
    month: number;
    roCountCustomer: number;
    roCountWarranty: number;
    roCountInternal: number;
    roCountServiceContract: number;
    roCountTotal: number;
    openROCount: number;
    openROAgeBuckets?: OpenROAgeBuckets;
    laborGrossCustomer: number;
    laborGrossWarranty: number;
    laborGrossInternal: number;
    laborGrossServiceContract: number;
    laborGrossTotal: number;
    laborSalesCustomer: number;
    laborSalesWarranty: number;
    laborSalesInternal: number;
    laborSalesServiceContract: number;
    laborSalesTotal: number;
    flatRateHoursCustomer: number;
    flatRateHoursWarranty: number;
    flatRateHoursInternal: number;
    flatRateHoursServiceContract: number;
    flatRateHoursTotal: number;
    elrCustomer: number | null;
    elrWarranty: number | null;
    elrInternal: number | null;
    elrServiceContract: number | null;
    elrTotal: number | null;
    avgHoursPerRoCustomer: number | null;
    avgHoursPerRoWarranty: number | null;
    avgHoursPerRoInternal: number | null;
    avgHoursPerRoServiceContract: number | null;
    avgHoursPerRoTotal: number | null;
    lastUpdated: string;
  }>;
  advisorCount: number;
  appointmentsNext5Days?: Array<{
    date: string;
    dayLabel: string;
    count: number;
    capacity?: number;
  }>;
  appointmentsNext5DaysCapacityTotal?: number;
  appointmentsNext5DaysAsOf?: string;
}

/**
 * Fetches service advisor performance data for a specific location and month
 * Admin/Executive/DepartmentManager endpoint
 */
export async function getServiceAdvisorPerformance(
  locationId: number,
  year: number,
  month: number
): Promise<ApiResponse<ServiceAdvisorPerformanceResponse>> {
  return apiRequest<ServiceAdvisorPerformanceResponse>(
    `/service-advisor-performance?locationId=${locationId}&year=${year}&month=${month}`
  );
}

/**
 * Triggers a synchronous refresh of service advisor data from DealerBuilt.
 * Admin-only. Refreshes data for a single location/month.
 */
export async function refreshServiceAdvisorData(
  locationId: number,
  year: number,
  month: number
): Promise<ApiResponse<{ locationsProcessed: number; monthsLoaded: number; advisorsLoaded: number; errors: string[] }>> {
  return apiRequest('/service-advisor-performance/refresh', {
    method: 'POST',
    body: JSON.stringify({ locationId, year, month }),
  });
}

/**
 * Technician Performance API Response
 * Matches the backend response structure from GET /technician-performance
 */
export interface TechnicianPerformanceResponse {
  locationId: number;
  year: number;
  month: number;
  summary: {
    techCount: number;
    totalFlatRateHours: number;
    totalLaborSales: number;
    totalLaborCost: number;
    totalLaborGross: number;
    lastUpdated: string;
  } | null;
  technicians: Array<{
    techId: string;
    techName: string;
    techRate: number;
    locationId: number;
    year: number;
    month: number;
    flatRateHoursTotal: number;
    laborSalesTotal: number;
    laborCostTotal: number;
    laborGrossTotal: number;
    elrTotal: number | null;
    costPerHourTotal: number | null;
    grossPerHourTotal: number | null;
  }>;
  dailyByTech: Record<string, Array<{
    date: string;
    flatRateHours: number;
    laborSales: number;
    laborCost: number;
    laborGross: number;
    jobCount: number;
  }>>;
  techCount: number;
}

/**
 * Fetches technician performance data for a specific location and month
 * Admin-only endpoint
 */
export async function getTechnicianPerformance(
  locationId: number,
  year: number,
  month: number
): Promise<ApiResponse<TechnicianPerformanceResponse>> {
  return apiRequest<TechnicianPerformanceResponse>(
    `/technician-performance?locationId=${locationId}&year=${year}&month=${month}`
  );
}

/**
 * Triggers a synchronous refresh of technician data from DealerBuilt.
 * Admin-only. Refreshes data for a single location/month.
 */
export async function refreshTechnicianData(
  locationId: number,
  year: number,
  month: number
): Promise<ApiResponse<{ locationsProcessed: number; monthsLoaded: number; techniciansLoaded: number; errors: string[] }>> {
  return apiRequest('/technician-performance/refresh', {
    method: 'POST',
    body: JSON.stringify({ locationId, year, month }),
  });
}

/**
 * Parts Counter Performance API Response
 * Matches the backend response structure from GET /parts-counter-performance
 */
export interface PartsCounterPerformanceResponse {
  locationId: number;
  year: number;
  month: number;
  summary: {
    personCount: number;
    totalSales: number;
    totalCost: number;
    totalGross: number;
    totalTransactions: number;
    totalLostSales: number;
    lastUpdated: string;
  } | null;
  persons: Array<{
    employeeId: string;
    employeeName: string;
    locationId: number;
    year: number;
    month: number;
    salesTotal: number;
    costTotal: number;
    grossTotal: number;
    gpPctTotal: number | null;
    transactionCount: number;
    lostSalesCount: number;
    counterRetail: { sales: number; cost: number; gross: number; gpPct: number | null };
    counterWholesale: { sales: number; cost: number; gross: number; gpPct: number | null };
    customerParts: { sales: number; cost: number; gross: number; gpPct: number | null };
    internalParts: { sales: number; cost: number; gross: number; gpPct: number | null };
    warrantyParts: { sales: number; cost: number; gross: number; gpPct: number | null };
    other: { sales: number; cost: number; gross: number; gpPct: number | null };
  }>;
  personCount: number;
}

/**
 * Fetches parts counter performance data for a specific location and month
 */
export async function getPartsCounterPerformance(
  locationId: number,
  year: number,
  month: number
): Promise<ApiResponse<PartsCounterPerformanceResponse>> {
  return apiRequest<PartsCounterPerformanceResponse>(
    `/parts-counter-performance?locationId=${locationId}&year=${year}&month=${month}`
  );
}

/**
 * Triggers a synchronous refresh of parts counter data from DealerBuilt.
 * Admin-only. Refreshes data for a single location/month.
 */
export async function refreshPartsCounterData(
  locationId: number,
  year: number,
  month: number
): Promise<ApiResponse<{ locationsProcessed: number; monthsLoaded: number; personsLoaded: number; errors: string[] }>> {
  return apiRequest('/parts-counter-performance/refresh', {
    method: 'POST',
    body: JSON.stringify({ locationId, year, month }),
  });
}

// ==========================================
// Sales Manager Performance
// ==========================================

export interface SalesManagerPerformanceResponse {
  locationId: number;
  year: number;
  month: number;
  summary: any;
  managers: any[];
  managerCount: number;
}

/**
 * Fetches sales manager performance data for a specific location and month
 */
export async function getSalesManagerPerformance(
  locationId: number,
  year: number,
  month: number
): Promise<ApiResponse<SalesManagerPerformanceResponse>> {
  return apiRequest<SalesManagerPerformanceResponse>(
    `/sales-manager-performance?locationId=${locationId}&year=${year}&month=${month}`
  );
}

/**
 * Triggers a synchronous refresh of sales manager data from DealerBuilt.
 * Admin-only. Refreshes data for a single location/month.
 */
export async function refreshSalesManagerData(
  locationId: number,
  year: number,
  month: number
): Promise<ApiResponse<{ locationsProcessed: number; monthsLoaded: number; managersLoaded: number; errors: string[] }>> {
  return apiRequest('/sales-manager-performance/refresh', {
    method: 'POST',
    body: JSON.stringify({ locationId, year, month }),
  });
}

/**
 * One sales team's block on the Salesperson Performance card (team = sales
 * manager, Configuration → Sales Teams). The trailing Unassigned bucket uses
 * an empty managerId. Absent on responses from before team grouping shipped.
 */
export interface SalespersonTeamSectionResponse {
  managerId: string;
  managerName: string;
  salespeople: any[];
  subtotals: any;
}

export interface SalespersonPerformanceResponse {
  locationId: number;
  year: number;
  month: number;
  summary: any;
  salespeople: any[];
  salespersonCount: number;
  teams?: SalespersonTeamSectionResponse[];
}

/**
 * Fetches per-salesperson performance data for a specific location and month.
 * Same data partition as sales-manager-performance — refresh button reuses
 * /sales-manager-performance/refresh.
 */
export async function getSalespersonPerformance(
  locationId: number,
  year: number,
  month: number
): Promise<ApiResponse<SalespersonPerformanceResponse>> {
  return apiRequest<SalespersonPerformanceResponse>(
    `/salesperson-performance?locationId=${locationId}&year=${year}&month=${month}`
  );
}

export interface FiManagerPerformanceResponse {
  locationId: number;
  year: number;
  month: number;
  summary: any;
  fiManagers: any[];
  fiManagerCount: number;
}

/**
 * Fetches per-F&I-manager performance data for a specific location and month.
 * Same data partition as sales-manager-performance — refresh button reuses
 * /sales-manager-performance/refresh.
 */
export async function getFiManagerPerformance(
  locationId: number,
  year: number,
  month: number
): Promise<ApiResponse<FiManagerPerformanceResponse>> {
  return apiRequest<FiManagerPerformanceResponse>(
    `/fi-manager-performance?locationId=${locationId}&year=${year}&month=${month}`
  );
}

/** Per-salesperson Good Leads MTD count (touched-attribution via VinSolutions). */
export interface SalesPersonLeadsBreakdown {
  userId: number;
  displayName: string;
  /** DealerBuilt sales rep ID, when known via VinSolutions useraccess.DMSSalesRepID. */
  dmsSalesRepId?: string;
  mtdLeads: number;
  /** mtdLeads bucketed by lead origination date (Eastern), key = YYYY-MM-DD. */
  dailyLeads?: Record<string, number>;
  /** Completed showroom-visit counts by visit date (Eastern) — any lead, be-backs count. */
  dailyVisits?: Record<string, number>;
  /** Distinct Active leads (90-day origination window) currently being worked. */
  activeLeadsWorking?: number;
}

/**
 * Pacing metrics for the Forecast page Sales Department Analysis card.
 * Mirrors `SalesLeadsPacingMetrics` in `lambda/vin-leads/shared/types.ts`.
 */
export interface SalesLeadsPacingMetrics {
  mtdLeads: number;
  mtdUnits: number;
  closingRatioPct: number | null;
  unitGoal: number;
  avgLeadsPerCalDay: number;
  projectedEomLeads: number;
  /** Leads needed by EOM to hit the unit goal at the 6-mo rolling close rate. */
  totalLeadsNeeded: number | null;
  /** Shortfall vs projected EOM. Negative = ahead of pace. */
  incrementalLeadsNeeded: number | null;
  /** 6-month rolling pooled close (Σunits / Σleads over the 6 completed prior months). */
  rollingClosingRatioPct: number | null;
  /** How many of the 6 rolling months actually had VIN_LEADS data. */
  rollingMonthsWithData: number;
  newDeptGrossVariancePct: number | null;
  usedDeptGrossVariancePct: number | null;
  daysInMonth: number;
  daysElapsed: number;
}

export interface SalesLeadsPacingResponse {
  locationId: number;
  locationName: string;
  year: number;
  month: number;
  /** Total Good Leads month-to-date (sales-relevant types only). */
  mtdGoodLeads: number;
  /** Daily lead counts keyed by Eastern YYYY-MM-DD. */
  dailyGoodLeads: Record<string, number>;
  /** Counts grouped by VinSolutions LeadSourceGroupName. */
  bySourceGroup: Record<string, number>;
  /** Per-salesperson Good Leads attribution. Empty array if loader hasn't written it yet. */
  salesPersonBreakdown?: SalesPersonLeadsBreakdown[];
  /** Most recent lead origination date observed in the dataset. */
  dataAsOfDate: string;
  /** ISO timestamp when the loader last wrote this record. */
  lastUpdatedAt: string;
  /** Pacing math (only present for single-location requests; absent when locationId=0). */
  pacing?: SalesLeadsPacingMetrics;
}

/**
 * Fetches MTD Good Leads counts for a specific location and month.
 * Sourced from VinSolutions CloudStore via the nightly/hourly vin-leads-loader.
 */
export async function getSalesLeadsPacing(
  locationId: number,
  year: number,
  month: number
): Promise<ApiResponse<SalesLeadsPacingResponse>> {
  return apiRequest<SalesLeadsPacingResponse>(
    `/sales-leads-pacing?locationId=${locationId}&year=${year}&month=${month}`
  );
}

// ============================================
// SALES FUNNEL (past 5 working days / next 5 calendar days per salesperson)
// ============================================

/**
 * One salesperson's Lead → Visit → Sale funnel row.
 * Mirrors `SalesFunnelRow` in `lambda/shared/sales-funnel.ts`.
 */
export interface SalesFunnelRow {
  name: string;
  vinUserId?: number;
  dmsSalesRepId?: string;
  /** Good Leads originated in the past 5 working days. */
  leads: number;
  /** Completed showroom visits in the past 5 working days (be-backs count). */
  visits: number;
  /** Appointments on past-window days BEFORE the report date (report-date appts count as scheduled). */
  apptsPast: number;
  /** Retail units sold in the past 5 working days (N+U, wholesale/swap excluded). Can be fractional — each rep on a 2-salesperson deal gets 0.5. */
  sales: number;
  visitsPerLeadPct: number | null;
  salesPerVisitPct: number | null;
  salesPerLeadPct: number | null;
  /** Distinct Active leads currently being worked. null when unknown. */
  activeLeads: number | null;
  /** Appointments scheduled for the report date + next 2 calendar days (first 3 days of the 5-day outlook). */
  apptsNext3: number;
  /** Appointments scheduled for the report date + next 4 calendar days (the full 5-day outlook). */
  apptsNext5: number;
  /** Times/reasons for report-date + next-window appointments, keyed by YYYY-MM-DD. */
  apptDetailsByDate?: Record<string, AppointmentDetailView[]>;
}

export interface SalesFunnelTotals {
  leads: number;
  visits: number;
  apptsPast: number;
  sales: number;
  visitsPerLeadPct: number | null;
  salesPerVisitPct: number | null;
  salesPerLeadPct: number | null;
  activeLeads: number;
  apptsNext3: number;
  apptsNext5: number;
}

/**
 * Funnel rows bucketed into one sales team (team = sales manager, configured
 * in Configuration → Sales Teams). Mirrors `SalesTeamSection` in
 * `lambda/shared/sales-funnel.ts`.
 */
export interface SalesTeamSection {
  /** Sales manager id; '' for the Unassigned bucket. */
  managerId: string;
  /** Team display name = the manager's name; 'Unassigned' for the bucket. */
  managerName: string;
  rows: SalesFunnelRow[];
  subtotals: SalesFunnelTotals;
}

export interface SalesFunnelResponse {
  locationId: number;
  reportDate: string;
  /** 5 working days, oldest → newest (last = report date when it's a working day). */
  pastDates: string[];
  /** Next 4 calendar days after the report date, soonest → latest (report date + these = the 5-day outlook window). */
  nextDates: string[];
  rows: SalesFunnelRow[];
  totals: SalesFunnelTotals;
  /** Rows grouped by sales team, sorted by team name with Unassigned last. */
  teams: SalesTeamSection[];
  lastUpdatedAt: { leads?: string; appointments?: string };
}

/** Fetches the per-salesperson Sales Funnel for one location around a date. */
export async function getSalesFunnel(
  locationId: number,
  date?: string,
): Promise<ApiResponse<SalesFunnelResponse>> {
  const qs = `locationId=${locationId}${date ? `&date=${date}` : ''}`;
  return apiRequest<SalesFunnelResponse>(`/sales-funnel?${qs}`);
}

// ============================================
// SALES APPOINTMENTS (5-DAY FORWARD OUTLOOK)
// ============================================

/** Appointment count for a single day in the 5-day window. */
export interface DayAppointmentCount {
  /** YYYY-MM-DD */
  date: string;
  /** Display label, e.g. "Today" or "Wed 4/9". */
  dayLabel: string;
  /** Count of scheduled appointments for the day. */
  scheduled: number;
}

/** A single appointment's display detail (time + reason). */
export interface AppointmentDetailView {
  /** "h:mm AM/PM" in Eastern time. */
  time: string;
  /** "Sales Appointment", "Delivery", "Other". */
  reason: string;
  /** Optional appointment subject (used for hover tooltip). */
  subject?: string;
}

/** Per-salesperson 5-day appointment breakdown. */
export interface SalesPersonDayCount {
  userId: number;
  userName: string;
  /** Parallel array with the 5-day window — count for each day. */
  days: number[];
  /** Sum across the 5 days. */
  total: number;
  /** Optional appointment-detail list per day, parallel to `days`. */
  details?: AppointmentDetailView[][];
}

/** 5-day forward appointment outlook for a single location. */
export interface SalesAppointmentsResponse {
  locationId: number;
  locationName: string;
  /** Today + next 4 days. */
  days: DayAppointmentCount[];
  /** Sum across all 5 days. */
  total: number;
  /** Per-salesperson breakdown (omitted/empty when no appointments). */
  salesPeople?: SalesPersonDayCount[];
  /** YYYY-MM-DD that the 5-day window starts on. */
  reportDate: string;
  /** ISO timestamp of the most recent loader write. */
  lastUpdatedAt?: string;
}

/**
 * Fetches the 5-day VinSolutions appointment outlook for a single location.
 * Locations 12 and 44 don't have VinSolutions data — the API returns an
 * empty-shaped response with HTTP 200.
 */
export async function getSalesAppointments(
  locationId: number,
  date?: string,
): Promise<ApiResponse<SalesAppointmentsResponse>> {
  const qs = `locationId=${locationId}${date ? `&date=${date}` : ''}`;
  return apiRequest<SalesAppointmentsResponse>(`/sales-appointments?${qs}`);
}

// ============================================
// INVENTORY AGING (Forecast NEW dept "Inventory" card)
// ============================================

export interface InventoryAgingOldestUnit {
  ageDays: number;
  year: number | null;
  make: string | null;
  model: string | null;
  stockNumber: string | null;
}

export interface InventoryAgingBrand {
  totalUnits: number;
  units90Plus: number;
  pct90Plus: number;
  avgAgeDays: number;
  oldestUnit: InventoryAgingOldestUnit | null;
}

export interface InventoryAgingResponse {
  locationId: number;
  /** Date the snapshot was actually written (may be earlier than `requestedDate` on fallback). */
  snapshotDate: string;
  /** Date the caller asked for (defaults to today Eastern). */
  requestedDate: string;
  totalNewUnits: number;
  totalUnits90Plus: number;
  brands: Record<string, InventoryAgingBrand>;
  unitsWithBadReceivedDate: number;
  /** False when no snapshot has been recorded for this location yet (or no-new-vehicle locations). */
  available: boolean;
}

export async function getInventoryAging(
  locationId: number,
  date?: string,
): Promise<ApiResponse<InventoryAgingResponse>> {
  const qs = `locationId=${locationId}${date ? `&date=${date}` : ''}`;
  return apiRequest<InventoryAgingResponse>(`/inventory-aging?${qs}`);
}

// Used-vehicle inventory aging — totals only, no brand breakdown, 60-day threshold.
export interface UsedInventoryAgingResponse {
  locationId: number;
  /** Date the snapshot was actually written (may be earlier than `requestedDate` on fallback). */
  snapshotDate: string;
  /** Date the caller asked for (defaults to today Eastern). */
  requestedDate: string;
  totalUsedUnits: number;
  totalUnits60Plus: number;
  pct60Plus: number;
  avgAgeDays: number;
  oldestUnit: InventoryAgingOldestUnit | null;
  unitsWithBadReceivedDate: number;
  /** False when no snapshot has been recorded for this location yet. */
  available: boolean;
}

export async function getUsedInventoryAging(
  locationId: number,
  date?: string,
): Promise<ApiResponse<UsedInventoryAgingResponse>> {
  const qs = `locationId=${locationId}${date ? `&date=${date}` : ''}`;
  return apiRequest<UsedInventoryAgingResponse>(`/used-inventory-aging?${qs}`);
}

// vAuto Look to Book — Appraiser breakdown for one location, past 60 days.
// Source: vAuto Appraisal Summary Report
// https://profittime.vauto.app.coxautoinc.com/Va/Dashboard/Provision.aspx?tab=EnterpriseInventory
export interface VAutoLookToBookAppraiser {
  name: string;
  appraisals: number;
  takenIn: number;
  lookToBook: number;
}

export interface VAutoLookToBookResponse {
  locationId: number;
  vautoStoreName?: string;
  snapshotDate: string;
  requestedDate: string;
  reportDate?: string;
  reportGeneratedAt?: string;
  windowDays: number;
  totalAppraisals: number;
  totalTakenIn: number;
  totalLookToBook: number;
  appraisers: VAutoLookToBookAppraiser[];
  sourceLabel?: string;
  sourceUrl?: string;
  /** False when no snapshot has been recorded for this location yet (or untracked locations). */
  available: boolean;
}

export async function getVautoLookToBook(
  locationId: number,
  date?: string,
): Promise<ApiResponse<VAutoLookToBookResponse>> {
  const qs = `locationId=${locationId}${date ? `&date=${date}` : ''}`;
  return apiRequest<VAutoLookToBookResponse>(`/vauto-look-to-book?${qs}`);
}

/** Deal detail for the Forecast audit modal */
export interface DealDetail {
  stockNumber: string;
  dealNumber: string;
  customerLastName: string;
  saleDate: string;
  make: string;
  status: 'Processed' | 'Unprocessed' | 'Swap' | 'Wholesale';
  totalGross: number;
}

export interface DealDetailsResponse {
  success: boolean;
  locationId: number;
  locationName: string;
  year: number;
  month: number;
  department: string;
  deals: DealDetail[];
  totalGross: number;
  totalUnits: number;
}

export async function getDealDetails(
  locationId: number,
  year: number,
  month: number,
  department: 'NEW_VEHICLE' | 'USED_VEHICLE'
): Promise<ApiResponse<DealDetailsResponse>> {
  return apiRequest<DealDetailsResponse>(
    `/deal-details?locationId=${locationId}&year=${year}&month=${month}&department=${department}`
  );
}

// ============================================
// REPORTS
// ============================================

export interface RunReportResponse {
  reportId: string;
  locationId: number;
  startDate: string;
  endDate: string;
  data: unknown;
}

export async function runReport(
  reportId: string,
  locationId: number,
  startDate: string,
  endDate: string
): Promise<ApiResponse<RunReportResponse>> {
  return apiRequest<RunReportResponse>(
    `/reports/run?reportId=${encodeURIComponent(reportId)}&locationId=${locationId}&startDate=${startDate}&endDate=${endDate}`
  );
}

export async function runWholesaleComparison(
  reportId: string,
  locationIds: number[],
  year: number,
  month: number
): Promise<ApiResponse<RunReportResponse>> {
  return apiRequest<RunReportResponse>(
    `/reports/run?reportId=${encodeURIComponent(reportId)}&locationIds=${locationIds.join(',')}&year=${year}&month=${month}`
  );
}

/** Runs a multi-location single-date report (Sales Team Report). */
export async function runSalesTeamFunnelReport(
  reportId: string,
  locationIds: number[],
  date: string
): Promise<ApiResponse<RunReportResponse>> {
  return apiRequest<RunReportResponse>(
    `/reports/run?reportId=${encodeURIComponent(reportId)}&locationIds=${locationIds.join(',')}&date=${date}`
  );
}

/**
 * Runs the multi-location Sales Team Report over a DATE RANGE (week-column
 * mode). The response additionally carries `periods` plus per-week counts on
 * every row and totals object; see types/salesTeamFunnel.ts.
 */
export async function runSalesTeamFunnelReportRange(
  reportId: string,
  locationIds: number[],
  startDate: string,
  endDate: string
): Promise<ApiResponse<RunReportResponse>> {
  return apiRequest<RunReportResponse>(
    `/reports/run?reportId=${encodeURIComponent(reportId)}&locationIds=${locationIds.join(',')}` +
    `&startDate=${startDate}&endDate=${endDate}`
  );
}
